import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import DepotDashboard from "./pages/DepotDashboard";
import Inventory from "./pages/Inventory";
import InventoryHQ from "./pages/InventoryHQ";
import Analytics from "./pages/Analytics";
import Upload from "./pages/Upload";
import MasterData from "./pages/MasterData";
import BufferReview from "./pages/BufferReview";
import Orders from "./pages/Orders";
import DeliveredOrders from "./pages/DeliveredOrders";
import BulkOrders from "./pages/BulkOrders";
import DepotManagement from "./pages/DepotManagement";
import UserManagement from "./pages/UserManagement";
import SalespersonView from "./pages/SalespersonView";
import TransferRecommendations from "./pages/TransferRecommendations";
import Settings from "./pages/Settings";
import ItemMasters from "./pages/ItemMasters";
import ProductionProgram from "./pages/ProductionProgram";
import Layout from "./components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Axios interceptor: auto-append hide_non_running param when preference is on
axios.interceptors.request.use((config) => {
  if (config.method === 'get' && localStorage.getItem('hideNonRunning') === 'true') {
    const separator = config.url.includes('?') ? '&' : '?';
    config.url = config.url + separator + 'hide_non_running=true';
  }
  return config;
});
const API = `${BACKEND_URL}/api`;

// Protected Route wrapper
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-slate-500">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Salesperson should only access their dedicated view
  if (user.role === 'salesperson') {
    return <Navigate to="/salesperson" replace />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/depot-dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Salesperson Route wrapper (no layout, mobile-optimized)
const SalespersonRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-slate-500">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Non-salesperson users should not access this page
  if (user.role !== 'salesperson') {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Transfer Recommendations Route (Admin + Jaipur depot only)
const TransferRecommendationsRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-slate-500">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Salesperson should only access their dedicated view
  if (user.role === 'salesperson') {
    return <Navigate to="/salesperson" replace />;
  }

  // Only Admin and Jaipur depot can access
  const isAdmin = user.role === 'admin';
  const isJaipurDepot = user.role === 'depot' && 
    user.depot_name?.toLowerCase().includes('jaipur');
  
  if (!isAdmin && !isJaipurDepot) {
    return <Navigate to="/depot-dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Auto-redirect based on role
const AuthRedirect = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-slate-500">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role === 'salesperson') {
    return <Navigate to="/salesperson" replace />;
  }

  return user.role === 'admin' 
    ? <Navigate to="/dashboard" replace /> 
    : <Navigate to="/depot-dashboard" replace />;
};

function AppRoutes() {
  const { user } = useAuth();

  // Seed data on first load
  useEffect(() => {
    const seedData = async () => {
      try {
        await axios.post(`${API}/seed`);
      } catch (error) {
        // Seed might fail if already done, that's OK
      }
    };
    seedData();
  }, []);

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={user ? <AuthRedirect /> : <Login />} />
      
      {/* Root redirect */}
      <Route path="/" element={<AuthRedirect />} />

      {/* Salesperson Route - Mobile optimized, no layout */}
      <Route 
        path="/salesperson" 
        element={
          <SalespersonRoute>
            <SalespersonView />
          </SalespersonRoute>
        } 
      />
      
      {/* Admin Routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute adminOnly>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/master-data" 
        element={
          <ProtectedRoute adminOnly>
            <MasterData />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/buffer-review" 
        element={
          <ProtectedRoute adminOnly>
            <BufferReview />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/depots" 
        element={
          <ProtectedRoute adminOnly>
            <DepotManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/users" 
        element={
          <ProtectedRoute adminOnly>
            <UserManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/settings" 
        element={
          <ProtectedRoute adminOnly>
            <Settings />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/item-masters" 
        element={
          <ProtectedRoute>
            <ItemMasters />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/production-program" 
        element={
          <ProtectedRoute>
            <ProductionProgram />
          </ProtectedRoute>
        } 
      />
      
      {/* Depot Dashboard */}
      <Route 
        path="/depot-dashboard" 
        element={
          <ProtectedRoute>
            <DepotDashboard />
          </ProtectedRoute>
        } 
      />
      
      {/* Shared Routes (both admin and depot) */}
      <Route 
        path="/inventory" 
        element={
          <ProtectedRoute>
            <Inventory />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/inventory-hq" 
        element={
          <ProtectedRoute>
            <InventoryHQ />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/analytics" 
        element={
          <ProtectedRoute>
            <Analytics />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/upload" 
        element={
          <ProtectedRoute>
            <Upload />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/orders" 
        element={
          <ProtectedRoute>
            <Orders />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/delivered-orders" 
        element={
          <ProtectedRoute>
            <DeliveredOrders />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/bulk-orders" 
        element={
          <ProtectedRoute>
            <BulkOrders />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/transfer-recommendations" 
        element={
          <TransferRecommendationsRoute>
            <TransferRecommendations />
          </TransferRecommendationsRoute>
        } 
      />
      
      {/* Catch all */}
      <Route path="*" element={<AuthRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
